import {ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {DatePipe, DecimalPipe} from '@angular/common';
import {ToastrService} from 'ngx-toastr';
import {StracliScreenshot, StracliScreenshotScreen, ScreenshotStreamService} from './screenshot-stream.service';
import {
  StracliCmdService,
  StracliCommandType,
  StracliPongDto
} from '../stracli-cmd/stracli-cmd.service';
import {BaseurlService} from '../../services/baseurl.service';

@Component({
  selector: 'app-screenshot-stream',
  imports: [
    DatePipe,
    DecimalPipe
  ],
  templateUrl: './screenshot-stream.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './screenshot-stream.component.scss'
})
export class ScreenshotStreamComponent implements OnInit, OnDestroy {

  hostNames: string[] = [];
  live = true;
  sse: EventSource | null = null;

  private screenshots: Map<string, StracliScreenshot> = new Map<string, StracliScreenshot>();
  private onlineHosts: string[] = [];
  private summaryHosts: string[] = [];
  /** requests that are still running, so that slow responses do not pile up while screenshots keep arriving */
  private summaryPending = false;
  private fetching: Set<string> = new Set<string>();

  constructor(
    private screenshotStreamService: ScreenshotStreamService,
    private stracliCmdService: StracliCmdService,
    private notificationService: ToastrService,
    private zone: NgZone,
    private baseUrlService: BaseurlService,
  ) { }

  ngOnInit(): void {
    this.reload();
    this.stracliCmdService.getHosts().subscribe({
      next: (res: StracliPongDto[]) => {
        this.onlineHosts = res.map(it => it.host);
        this.updateHostNames();
      },
      error: err => this.notificationService.error('failed to retrieve hosts ' + JSON.stringify(err))
    });
    this.setupSse();
  }

  ngOnDestroy(): void {
    if (this.sse) {
      this.sse.close();
    }
  }

  reload(): void {
    this.refresh(true, true);
  }

  toggleLive(): void {
    this.live = !this.live;
  }

  screenshotOf(host: string): StracliScreenshot | undefined {
    return this.screenshots.get(host);
  }

  dataUrl(screenshot: StracliScreenshot, screen: StracliScreenshotScreen): string {
    return `data:image/${screenshot.format};base64,${screen.imageBase64}`;
  }

  startScreenshotStream(host: string): void {
    this.runCommand(host, StracliCommandType.START_SCREENSHOT_STREAM, 'screenshot stream started on ' + host);
  }

  endScreenshotStream(host: string): void {
    this.runCommand(host, StracliCommandType.END_SCREENSHOT_STREAM, 'screenshot stream ended on ' + host);
  }

  private runCommand(host: string, type: StracliCommandType, success: string): void {
    this.stracliCmdService.runCommand(host, type, {}).subscribe({
      next: () => this.notificationService.success(success),
      error: err => this.notificationService.error('Failed to execute command ' + JSON.stringify(err))
    });
  }

  private setupSse(): void {
    // the events carry no data, they only say that some host sent a new screenshot, the summaries tell us which one
    this.sse = new EventSource(this.baseUrlService.getBaseUrl() + '/stracliScreenshots/sse');
    this.sse.onmessage = () => {
      this.zone.run(() => {
        if (this.live) {
          this.refresh(false, false);
        }
      });
    };
  }

  /**
   * The summaries are cheap, the screenshots themselves are only fetched for the hosts that actually have a newer one.
   */
  private refresh(force: boolean, reportErrors: boolean): void {
    if (this.summaryPending) {
      return;
    }
    this.summaryPending = true;
    this.screenshotStreamService.getScreenshotSummaries().subscribe({
      next: res => {
        this.summaryPending = false;
        this.summaryHosts = res.map(it => it.host);
        res.forEach(summary => {
          const known = this.screenshots.get(summary.host);
          // a restarted stream begins at sequence 1 again, so the capture time decides which screenshot is newer
          if (force || !known || Date.parse(summary.captured) > Date.parse(known.captured)) {
            this.fetchScreenshot(summary.host);
          }
        });
        this.updateHostNames();
      },
      error: err => {
        this.summaryPending = false;
        if (reportErrors) {
          this.notificationService.error('failed to retrieve screenshots ' + JSON.stringify(err));
        }
      }
    });
  }

  private fetchScreenshot(host: string): void {
    if (this.fetching.has(host)) {
      return;
    }
    this.fetching.add(host);
    this.screenshotStreamService.getScreenshot(host).subscribe({
      next: res => {
        this.fetching.delete(host);
        const known = this.screenshots.get(host);
        if (!known || Date.parse(res.captured) >= Date.parse(known.captured)) {
          this.screenshots.set(host, res);
          this.updateHostNames();
        }
      },
      error: () => this.fetching.delete(host)
    });
  }

  private updateHostNames(): void {
    const names = new Set<string>([...this.onlineHosts, ...this.summaryHosts]);
    this.screenshots.forEach((_, host) => names.add(host));
    const updated = Array.from(names).sort((a, b) => a.localeCompare(b));
    if (updated.length !== this.hostNames.length || updated.some((it, i) => it !== this.hostNames[i])) {
      this.hostNames = updated;
    }
  }
}
